/**
 * Scale-anchor capture flow. Three input kinds per FR-D8.
 * - known_dimensions: free-form text ("back wall is 12 ft")
 * - wall_height: number (feet)
 * - letter_page: customer is asked to capture an extra photo with a
 *   letter-sized page in frame. We record the anchor first; the
 *   photo upload is a follow-on step the customer takes from the
 *   gallery.
 */

import { useState, type FormEvent } from "react";

import { createAnchor } from "@starz/shared/api/sdk.gen";
import type { AnchorKind, ScaleAnchor } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  /** Called when an anchor is saved. Use this to refresh state. */
  onCreated: (anchor: ScaleAnchor) => void;
  /** Caller may dismiss without saving. */
  onCancel?: () => void;
}

export function AnchorRecorder({ estimateId, onCreated, onCancel }: Props) {
  const [kind, setKind] = useState<AnchorKind>("known_dimensions");
  const [text, setText] = useState("");
  const [feet, setFeet] = useState("8");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    let valueText = "";
    if (kind === "known_dimensions") {
      valueText = text.trim();
      if (!valueText) {
        setError('Please describe a known length, e.g. "back wall 12 ft".');
        setSaving(false);
        return;
      }
    } else if (kind === "wall_height") {
      const f = parseFloat(feet);
      if (Number.isNaN(f) || f <= 0) {
        setError("Please enter a positive number of feet.");
        setSaving(false);
        return;
      }
      valueText = `${feet} ft`;
    } else {
      // letter_page — value_text is descriptive; the model uses 11 in
      valueText = "Letter-sized page (8.5 × 11 in) in frame";
    }

    const result = await createAnchor({
      estimateId,
      kind,
      valueText,
    });
    setSaving(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onCreated(result.data);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
            Scale anchor
          </legend>
          <p className="mt-1 text-sm text-warm-gray">
            Stella needs one of these so she can measure your space without inventing numbers.
          </p>
          <div className="mt-3 space-y-2">
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="anchor_kind"
                value="known_dimensions"
                checked={kind === "known_dimensions"}
                onChange={() => setKind("known_dimensions")}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-starz-charcoal">
                  Known dimension
                </span>
                <span className="block text-xs text-warm-gray">
                  Tell us a length you can confirm — e.g. the wall under the window is 12 ft.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="anchor_kind"
                value="wall_height"
                checked={kind === "wall_height"}
                onChange={() => setKind("wall_height")}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-starz-charcoal">
                  Ceiling / wall height
                </span>
                <span className="block text-xs text-warm-gray">
                  Standard rooms are about 8 ft. Measure if you're not sure.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="anchor_kind"
                value="letter_page"
                checked={kind === "letter_page"}
                onChange={() => setKind("letter_page")}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-starz-charcoal">
                  Letter page in frame
                </span>
                <span className="block text-xs text-warm-gray">
                  Tape a regular 8.5 × 11" sheet to a wall and take a photo showing it.
                </span>
              </span>
            </label>
          </div>
        </fieldset>
      </div>

      {kind === "known_dimensions" && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-starz-gold">
            What do you know?
          </label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. back wall is 12 ft"
            className="mt-1 w-full rounded-lg border border-starz-gold/20 bg-white px-4 py-2 text-base focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40"
            autoFocus
          />
        </div>
      )}

      {kind === "wall_height" && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-starz-gold">
            Ceiling height (feet)
          </label>
          <input
            type="number"
            value={feet}
            min="6"
            max="20"
            step="0.5"
            onChange={(e) => setFeet(e.target.value)}
            className="mt-1 w-32 rounded-lg border border-starz-gold/20 bg-white px-4 py-2 text-base focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40"
            autoFocus
          />
        </div>
      )}

      {kind === "letter_page" && (
        <p className="rounded-md border border-starz-gold/20 bg-starz-cream/40 p-3 text-sm text-starz-charcoal">
          Save this anchor first, then take a photo from the gallery showing a letter-sized sheet
          taped to the wall. Stella will use the page for scale.
        </p>
      )}

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-starz-gold/30 bg-white px-4 py-2 text-sm font-medium text-starz-charcoal hover:bg-starz-cream"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold text-starz-off-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save anchor"}
        </button>
      </div>
    </form>
  );
}
